// components/Admin/PresenterManager.tsx
//
// Admin-only presenter onboarding. Promote an audience member to `presenter`
// (they can then create quizzes and host their own + catalog quizzes) or
// demote a presenter back to `audience`. Admins are NOT manageable here —
// `admin` can never be set from a client (rules validate it away); promote
// admins manually in the Firebase console.
//
// This is a one-time get(), not a live listener: the roster only changes when
// someone signs in for the first time, and an always-on `users` subscription
// would re-download the node on every lastLogin write for nothing.
import { useEffect, useMemo, useState } from 'react';
import { ref, get, set } from 'firebase/database';
import { db } from '../../firebase';
import { useAuth } from '../../context/Auth/auth-context';
import { Role } from '../../types/authcontext.types';
import { seedPresenterProfile } from '../../services/presenter';

interface UserRecord {
  name?: string;
  email?: string;
  photoURL?: string;
  role?: Role;
  lastLogin?: string;
}

export const PresenterManager = () => {
  const { user: myUid } = useAuth();
  const [users, setUsers] = useState<Record<string, UserRecord>>({});
  const [search, setSearch] = useState('');
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const loadUsers = async () => {
    try {
      const snap = await get(ref(db, 'users'));
      setUsers((snap.val() ?? {}) as Record<string, UserRecord>);
    } catch {
      setError('Could not load users — are you signed in as admin?');
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRole = async (uid: string, role: 'presenter' | 'audience') => {
    setError('');
    setBusyUid(uid);
    try {
      await set(ref(db, `users/${uid}/role`), role);
      setUsers((prev) => ({ ...prev, [uid]: { ...prev[uid], role } }));
      // New presenters get a starter public page (presenterProfiles/{uid}) so
      // they appear in the crew/share pickers immediately. Never clobbers an
      // existing profile (re-promotion after a demotion keeps their brand),
      // and never blocks the promotion itself.
      if (role === 'presenter') {
        const u = users[uid];
        seedPresenterProfile(uid, u?.name, u?.photoURL).catch(() => {});
      }
    } catch {
      setError('Role update rejected — only admins may promote presenters.');
    } finally {
      setBusyUid(null);
    }
  };

  const { presenters, others } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = Object.entries(users).filter(
      ([, u]) =>
        !q ||
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
    return {
      presenters: matches.filter(([, u]) => u.role === 'presenter'),
      // Admins are shown (so the roster is complete) but not manageable.
      others: matches.filter(([, u]) => u.role !== 'presenter'),
    };
  }, [users, search]);

  const row = ([uid, u]: [string, UserRecord]) => {
    const isSelf = uid === myUid;
    const isAdmin = u.role === 'admin';
    const isPresenter = u.role === 'presenter';
    return (
      <div key={uid} className="card p-4 flex items-center gap-3">
        {u.photoURL ? (
          <img src={u.photoURL} alt="" className="w-9 h-9 rounded-full ring-2 ring-slate-200 shrink-0" />
        ) : (
          <span className="grid place-items-center w-9 h-9 rounded-full bg-slate-200 text-sm font-semibold shrink-0">
            {(u.name || u.email || '?').charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">
            {u.name || 'Unnamed'}
            {isSelf && <span className="text-slate-400 font-normal"> (you)</span>}
          </p>
          <p className="text-sm text-slate-500 truncate">{u.email}</p>
        </div>
        <span
          className={`chip shrink-0 ${
            isAdmin ? 'bg-ink text-accent' : isPresenter ? 'bg-tile-green text-white' : 'bg-ink/10 text-ink/60'
          }`}
        >
          {(u.role || 'audience').toUpperCase()}
        </span>
        {!isAdmin &&
          (isPresenter ? (
            <button
              onClick={() => setRole(uid, 'audience')}
              disabled={busyUid === uid}
              className="btn text-sm text-ink bg-ink/10 hover:bg-ink/15 shrink-0"
            >
              {busyUid === uid ? '…' : 'Demote'}
            </button>
          ) : (
            <button
              onClick={() => setRole(uid, 'presenter')}
              disabled={busyUid === uid}
              className="btn-primary text-sm shrink-0"
            >
              {busyUid === uid ? '…' : 'Make presenter'}
            </button>
          ))}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-extrabold font-display tracking-tight mb-1">Presenters</h1>
      <p className="text-sm text-slate-500 mb-4">
        Presenters can create their own quizzes and host anything in the catalog — but they can
        never edit catalog quizzes. Someone must sign in once before they can be promoted.
      </p>

      <input
        type="text"
        placeholder="Search by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none mb-5"
      />

      {error && <p className="text-tile-red font-medium mb-3" role="alert">{error}</p>}
      {!loaded && <p className="text-slate-400 text-center py-10">Loading users…</p>}

      {loaded && presenters.length > 0 && (
        <>
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
            Current presenters ({presenters.length})
          </h2>
          <div className="space-y-2 mb-6">{presenters.map(row)}</div>
        </>
      )}

      {loaded && (
        <>
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
            Everyone else ({others.length})
          </h2>
          <div className="space-y-2">
            {others.length === 0 ? (
              <p className="text-slate-400 text-center py-6">No matching users.</p>
            ) : (
              others.map(row)
            )}
          </div>
        </>
      )}
    </div>
  );
};
